import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ISTPersonActivity } from 'aa-ng';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { IResearchAtAGlanceMetric } from '../../models';
import { LoadingIndicatorService } from './loading-indicator.service';

@Injectable({ providedIn: 'root' })
export class ActivitiesService {
  private http = inject(HttpClient);
  private loadingIndicatorService = inject(LoadingIndicatorService);

  private _baseUrl = '/api/activities';
  private _queryActivitiessCache: {
    [query: string]: ISTPersonActivity[];
  } = {};

  search(
    query: string,
    institutionIds: number[] = null,
  ): Observable<ISTPersonActivity[]> {
    if (this._queryActivitiessCache[query]) {
      return of(this._queryActivitiessCache[query]);
    } else {
      const currentDate = new Date();
      const threeYearsAgoDate = new Date(
        currentDate.setFullYear(currentDate.getFullYear() - 3),
      );

      const searchObj = {
        query,
        institutionIds,
        earliestActivityDate: threeYearsAgoDate,
      };

      return this.http
        .post<ISTPersonActivity[]>(`${this._baseUrl}/search`, searchObj)
        .pipe(
          tap((activities) => {
            this._queryActivitiessCache[query] = activities;
          }),
        );
    }
  }

  getMetrics(
    activityTypeIds: number[],
    institutionIds: number[],
    unitId?: number,
    excludedGrantStatuses?: number[],
    forceIsLoadingState?: boolean,
  ): Observable<IResearchAtAGlanceMetric[]> {
    if (forceIsLoadingState) {
      this.loadingIndicatorService.forceLoadingState(true);
    }

    return this.http.post<IResearchAtAGlanceMetric[]>(
      `${this._baseUrl}/metrics`,
      {
        ActivityTypeIds: activityTypeIds,
        InstitutionIds: institutionIds,
        UnitId: unitId,
        excludedGrantStatuses: excludedGrantStatuses,
      },
    );
  }

  getRecent(
    pageIndex: number,
    pageSize: number,
    activityTypeId: number,
    institutionIds: number[],
    unitId?: number,
    excludedGrantStatuses?: number[],
  ): Observable<ISTPersonActivity[]> {
    return this.http.post<ISTPersonActivity[]>(
      `${this._baseUrl}/recentactivities`,
      {
        activityTypeId,
        institutionIds,
        unitId,
        pageIndex,
        pageSize,
        excludedGrantStatuses,
      },
    );
  }

  getActivitiesByUnit(
    personId: number,
    unitId: number,
    institutionIds?: number[],
    includeNonCollaborativeWorks = false,
    includeExtendedNetwork = false,
  ): Observable<ISTPersonActivity[]> {
    return this.http
      .post<
        ISTPersonActivity[]
      >(`${this._baseUrl}/unit/${includeNonCollaborativeWorks}/${includeExtendedNetwork}`, { unitId: unitId, personIds: [personId], institutionIds })
      .pipe(
        tap((activities) => {
          if (!includeNonCollaborativeWorks) {
            activities.forEach((activity) => {
              if (activity.contributors) {
                activity.contributors = activity.contributors.filter(
                  (c) => c.id !== personId,
                );
              }
            });
          }
        }),
      );
  }
}
